import React, { useMemo } from 'react';
import { useSettings } from '../../context/SettingsContext';
import { usePlayerComparison } from '../../context/PlayerComparisonContext';
import { Trash2 } from 'lucide-react';
import aflPlayerData from '../../assets/afl_player_data_2026.json';
import { formatWins, formatPct } from '../../utils/deadHeat';
import ImpliedOddsInfo from '../common/ImpliedOddsInfo';

// Marks a cell where the player leads that dataset within the comparison.
const LeaderCheck = ({ className = 'w-2.5 h-2.5 mt-0.5' }) => (
  <svg className={`${className} text-emerald-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronDown = ({ className = 'w-3 h-3' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
  </svg>
);

const winnerCellClass = (flag) => (flag ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : '');

const PlayerComparisonTable = ({ selectedPlayers = [], voteData, onRemovePlayer, groupId }) => {
  const { toggleMobileRow, isMobileRowExpanded } = usePlayerComparison();

  // Enabled dataset keys (count1..count5, betfair, espn, byb)
  const { enabledDatasets, getEnabledKeys } = useSettings();
  const enabledSourceKeys = getEnabledKeys();
  const enabledSourceSet = new Set(enabledSourceKeys);
  
  // Display order for tables (C1..C5 then external)
  const displayOrder = ['count1','count2','count3','count4','count5','betfair','espn','byb'];
  const enabledDisplayKeys = displayOrder.filter((k) => enabledSourceSet.has(k));
  const keyToLabel = { 
    count1: 'BG 1', count2: 'BG 2', count3: 'BG 3', count4: 'BG 4', count5: 'BG 5', 
    betfair: 'BETFAIR', espn: 'ESPN', byb: 'BYB' 
  };

  // Calculate average of enabled datasets for a player row
  const averageEnabledFromRow = (rowObj) => {
    const keys = enabledDisplayKeys;
    const n = keys.length;
    if (!n) return 0;
    let sum = 0;
    for (const k of keys) {
      const v = Number(rowObj?.[k] ?? 0);
      sum += isNaN(v) ? 0 : v;
    }
    return sum / n;
  };

  // Function to get player image based on name
  const getPlayerImage = (playerName) => {
    const player = aflPlayerData.find(p => p.player_name === playerName);
    return player ? player.image_src : null;
  };

  // Get player data for selected players
  const playersData = useMemo(() => {
    if (!voteData?.leaderboard || selectedPlayers.length === 0) return [];
    
    const players = selectedPlayers.map(playerName => {
      const playerData = voteData.leaderboard.find(p => p.name === playerName);
      if (!playerData) return null;
      
      // Calculate dynamic votes as average of enabled datasets
      const votes = averageEnabledFromRow(playerData);
      
      return {
        ...playerData,
        votes
      };
    }).filter(Boolean);

    // Sort by votes (highest to lowest)
    return players.sort((a, b) => (b.votes || 0) - (a.votes || 0));
  }, [selectedPlayers, voteData?.leaderboard, enabledSourceKeys]);

  // Find winning player for each enabled count
  const getWinningPlayerForCount = (countKey) => {
    if (playersData.length === 0) return null;
    
    let maxValue = -Infinity;
    let winningPlayers = [];
    
    for (const player of playersData) {
      const value = Number(player[countKey] ?? 0);
      if (value > maxValue) {
        maxValue = value;
        winningPlayers = [player.name];
      } else if (value === maxValue) {
        winningPlayers.push(player.name);
      }
    }
    
    return winningPlayers;
  };

  // Helper function for winner cell styling
  const isWinnerForCount = (playerName, countKey) => {
    const winners = getWinningPlayerForCount(countKey);
    return winners && winners.includes(playerName);
  };

  // Dead-heat win share for implied odds: 1 for an outright win, 1/n when n
  // compared players tie at the top of that count, 0 otherwise. A count where
  // nobody has votes (max 0) is not a win for anyone.
  const winShareForCount = (playerName, countKey) => {
    const winners = getWinningPlayerForCount(countKey);
    if (!winners || !winners.includes(playerName)) return 0;
    const top = playersData.find((p) => p.name === winners[0]);
    if (!(Number(top?.[countKey] ?? 0) > 0)) return 0;
    return 1 / winners.length;
  };

  const computeLeaderHits = (playerName) => {
    let hits = 0;
    for (const key of enabledDisplayKeys) hits += winShareForCount(playerName, key);
    return hits;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Mobile: simplified table (PLAYER, VOTES) with expandable rows */}
      <div className="md:hidden bg-slate-950/60 rounded-xl border border-slate-800 overflow-hidden flex-1">
        <div className="overflow-hidden">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[45%]" />
              <col className="w-[25%]" />
              <col className="w-[15%]" />
              <col className="w-[15%]" />
            </colgroup>
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className="text-left px-3 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">
                  Player
                </th>
                <th className="text-center px-2 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10">
                  Votes
                </th>
                <th className="text-center px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">
                  <span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span>
                </th>
                <th className="text-center px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">
                  
                </th>
              </tr>
            </thead>
            <tbody>
              {playersData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center">
                    <p className="text-slate-500 text-xs font-mono">Select players to begin comparison</p>
                  </td>
                </tr>
              ) : (
                playersData.map((player) => {
                  const playerImageSrc = getPlayerImage(player.name);
                  const isExpanded = isMobileRowExpanded(groupId, player.name);
                  
                  return (
                    <React.Fragment key={player.name}>
                      {/* Main row */}
                      <tr className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                        <td className="px-3 py-2">
                          <div className="flex items-center space-x-2">
                            {playerImageSrc ? (
                              <img 
                                src={playerImageSrc} 
                                alt={player.name}
                                className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                <span className="text-white font-bold text-xs">{player.image}</span>
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-white font-semibold truncate text-xs">
                                {player.name}
                              </div>
                              <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{player.team}</div>
                            </div>
                            <button
                              className={`w-6 h-6 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-200 ${isExpanded ? 'rotate-180 bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'border-slate-700/60 text-slate-400 hover:text-slate-200 hover:border-slate-600'}`}
                              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                              onClick={() => toggleMobileRow(groupId, player.name)}
                            >
                              <ChevronDown />
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center bg-emerald-500/[0.06]">
                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm">
                            {player.votes.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          {(() => {
                            // Effective wins across counts (ties share the win)
                            const leaderHits = computeLeaderHits(player.name);
                            const denom = enabledDisplayKeys.length || 1;
                            
                            const hitPct = (leaderHits / denom) * 100;
                            if (hitPct === 0) {
                              return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                            }
                            
                            const odds = (1 / (hitPct / 100)).toFixed(2);
                            return (
                              <div className="flex flex-col items-center justify-center gap-0.5">
                                <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs">{odds}</span>
                                <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(leaderHits)}/{denom}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <button
                            onClick={() => onRemovePlayer && onRemovePlayer(player.name)}
                            className="text-rose-400 hover:text-rose-300 transition-colors p-1 rounded-md hover:bg-rose-500/10"
                            title="Remove player from comparison"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>

                      {/* Expanded details row */}
                      {isExpanded && (
                        <tr className="bg-[#060b16] border-b border-slate-800/50">
                          <td colSpan={4} className="px-0 pb-0">
                            <div className="overflow-x-auto">
                              <table className="min-w-[600px] w-full">
                                <thead>
                                  <tr className="bg-slate-800/20">
                                    {/* Count headers (enabled only) */}
                                    {enabledDisplayKeys.map((key) => (
                                      <th key={`h-${key}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">
                                        {keyToLabel[key]}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    {/* Count values */}
                                    {enabledDisplayKeys.map((key) => {
                                      const isWinner = isWinnerForCount(player.name, key);
                                      return (
                                        <td key={`v-${key}`} className={`px-2 py-2 text-center ${winnerCellClass(isWinner)}`}>
                                          <div className="flex flex-col items-center">
                                            <span className="text-slate-400 font-mono tabular-nums text-xs">
                                              {typeof player[key] === 'number' ? player[key].toFixed(2) : (player[key] || '0.00')}
                                            </span>
                                            {isWinner && (
                                              <LeaderCheck />
                                            )}
                                          </div>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Desktop: full table (compressed so every column fits without side-scrolling) */}
      <div className="hidden md:block bg-slate-950/60 rounded-xl border border-slate-800 overflow-hidden flex-1">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className="text-left px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">
                  Player
                </th>
                {enabledDisplayKeys.map((key) => (
                  <th key={key} className="text-center px-1 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">
                    {key === 'betfair' ? (
                      <>
                        <span className="hidden xl:inline">BETFAIR</span>
                        <span className="xl:hidden">BF</span>
                      </>
                    ) : (
                      keyToLabel[key]
                    )}
                  </th>
                ))}
                <th className="text-center px-2 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10">
                  Votes
                </th>
                <th className="text-center px-1 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] w-16">
                  <span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span>
                </th>
                <th className="text-center px-1 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] w-10">
                  
                </th>
              </tr>
            </thead>
            <tbody>
              {playersData.length === 0 ? (
                <tr>
                  <td colSpan={enabledDisplayKeys.length + 4} className="px-3 py-8 text-center">
                    <p className="text-slate-500 text-xs font-mono">Select players to begin comparison</p>
                  </td>
                </tr>
              ) : (
                playersData.map((player, index) => {
                const playerImageSrc = getPlayerImage(player.name);
                
                return (
                  <tr 
                    key={player.name}
                    className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors"
                  >
                    {/* Player */}
                    <td className="px-2 py-1.5">
                      <div className="flex items-center space-x-2">
                        {playerImageSrc ? (
                          <img 
                            src={playerImageSrc} 
                            alt={player.name}
                            className="w-7 h-7 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60"
                          />
                        ) : (
                          <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                            <span className="text-white font-bold text-xs">{player.image}</span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1 max-w-[110px] xl:max-w-[170px]">
                          <div className="text-white font-semibold truncate text-xs">
                            {player.name}
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{player.team}</div>
                        </div>
                      </div>
                    </td>

                    {/* Count columns - only show enabled datasets */}
                    {enabledDisplayKeys.map((key) => {
                      const isWinner = isWinnerForCount(player.name, key);
                      return (
                        <td key={key} className={`px-1 py-1.5 text-center ${winnerCellClass(isWinner)}`}>
                          <div className="flex flex-col items-center">
                            <span className="text-slate-400 font-mono tabular-nums text-[11px] xl:text-xs">
                              {typeof player[key] === 'number' ? player[key].toFixed(2) : (player[key] || '0.00')}
                            </span>
                            {isWinner && (
                              <LeaderCheck />
                            )}
                          </div>
                        </td>
                      );
                    })}

                    {/* Votes - dynamic average of enabled datasets */}
                    <td className="px-2 py-1.5 text-center bg-emerald-500/[0.06]">
                      <span className="text-emerald-300 font-mono font-bold tabular-nums text-[13px] xl:text-sm">
                        {player.votes.toFixed(2)}
                      </span>
                    </td>

                    {/* Implied Odds */}
                    <td className="px-1 py-1.5 text-center w-16">
                      {(() => {
                        // Effective wins across counts (ties share the win)
                        const leaderHits = computeLeaderHits(player.name);
                        const denom = enabledDisplayKeys.length || 1;
                        
                        const hitPct = (leaderHits / denom) * 100;
                        if (hitPct === 0) {
                          return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
                        }
                        
                        const odds = (1 / (hitPct / 100)).toFixed(2);
                        return (
                          <div className="flex flex-col items-center justify-center gap-0.5">
                            <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs">{odds}</span>
                            <span className="text-slate-500 text-[10px] font-mono tabular-nums whitespace-nowrap">
                              {formatWins(leaderHits)}/{denom}<span className="hidden xl:inline"> ({formatPct(hitPct)}%)</span>
                            </span>
                          </div>
                        );
                      })()}
                    </td>

                    {/* Remove Button */}
                    <td className="px-1 py-1.5 text-center">
                      <button
                        onClick={() => onRemovePlayer && onRemovePlayer(player.name)}
                        className="text-rose-400 hover:text-rose-300 transition-colors p-1 rounded-md hover:bg-rose-500/10"
                        title="Remove player from comparison"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PlayerComparisonTable;
